import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useMemo, useState } from 'react';
import { AuditStatus, AuditType } from '@amzn/gbsa-helios-typescript-client';
import { Spinner } from '@cloudscape-design/components';
import { sumBy } from 'lodash';
import { useAuditStatsQuery } from 'src/data/api/helios-service-ts/audit/queries/useAuditStatsQuery';
import { AUDIT_STATUS_LABELS } from '../../contstant/commonLabels';
import { STATUS_BG_COLORS } from '../../utils/colors';
import StatusIndicator from '../StatusIndicator';
import FilterButton from './FilterButton';
import { SearchHomeRequest } from './HomeFilter';
import { useUserDetails } from 'src/providers/UserProvider';

const AuditOverviewSection = () => {
  // Get user profile data and audit store functions
  const { userPreferences } = useUserDetails();
  const [searchParams, setSearchParams] = useState<SearchHomeRequest>({});

  const { data: auditStats, isLoading: auditStatsLoading } = useAuditStatsQuery(
    {
      divisions: searchParams.divisions ?? undefined,
      auditTypes: (searchParams.auditTypes as AuditType[]) ?? undefined,
      businessUnitId: userPreferences.profileSettings.businessUnit,
      locations: userPreferences.profileSettings.businessUnitLocationPreferences,
      statuses: [
        AuditStatus.NOT_STARTED,
        AuditStatus.IN_PROGRESS,
        AuditStatus.COMPLETED,
        AuditStatus.EXPIRED,
      ],
    },
    {
      enabled:
        !!userPreferences.profileSettings.businessUnit &&
        !!userPreferences.profileSettings.businessUnitLocationPreferences,
      select(data) {
        const divisionNames = new Set<string>();
        const divisions = Object.values(data.auditTypes).flatMap((aT) =>
          Object.entries(aT.divisions).map(([divisionName, division]) => {
            divisionNames.add(divisionName);
            return {
              ...division,
              divisionName,
            };
          }),
        );

        const metrics = divisions.reduce(
          (acc, d) => {
            acc.completedAudits += d.statuses[AuditStatus.COMPLETED] || 0;
            acc.inProgressAudits += d.statuses[AuditStatus.IN_PROGRESS] || 0;
            acc.scheduledAudits += d.statuses[AuditStatus.SCHEDULED] || 0;
            acc.expiredAudits += d.statuses[AuditStatus.EXPIRED] || 0;
            acc.notStartedAudits += d.statuses[AuditStatus.NOT_STARTED] || 0;
            acc.totalAudits += d.totalCount;
            return acc;
          },
          {
            completedAudits: 0,
            inProgressAudits: 0,
            scheduledAudits: 0,
            expiredAudits: 0,
            notStartedAudits: 0,
            totalAudits: 0,
          },
        );
        return {
          ...data,
          metrics,
          divisions,
          divisionNames: Array.from(divisionNames),
        };
      },
    },
  );

  const divisions = auditStats?.divisions ?? [];

  // Build chart series data
  const series = useMemo(() => {
    if (!auditStats) return [];

    // Configure status types with colors and labels
    const statusConfigs = [
      AuditStatus.COMPLETED,
      AuditStatus.IN_PROGRESS,
      AuditStatus.NOT_STARTED,
      AuditStatus.EXPIRED,
    ]
      .filter((status) => STATUS_BG_COLORS[status as keyof typeof STATUS_BG_COLORS])
      .map((status) => ({
        key: status,
        title: AUDIT_STATUS_LABELS[status],
        color: STATUS_BG_COLORS[status as keyof typeof STATUS_BG_COLORS],
      }));

    // Create chart series for each status
    return statusConfigs.map(({ key, title, color }) => ({
      title,
      type: 'bar' as const,
      data: auditStats.divisionNames.map((divisionName) => ({
        x: divisionName,
        y: sumBy(
          divisions.filter((d) => d.divisionName === divisionName),
          (d) => d.statuses[key] || 0,
        ),
      })),
      color,
    }));
  }, [divisions]);

  return (
    <Container
      data-testid="audit-overview-container"
      header={
        <Header
          data-testid="audit-overview-header"
          variant="h2"
          actions={
            // Filter button for search functionality
            <FilterButton
              data-testid="audit-filter-button"
              onSearchChange={function (searchParams: SearchHomeRequest): void {
                setSearchParams(searchParams);
              }}
              searchParams={searchParams}
              type={'audit'}
            />
          }
        >
          Audit Overview
        </Header>
      }
    >
      {/* Total audits indicator */}
      <StatusIndicator
        data-testid="total-audits-indicator"
        value={auditStats?.metrics.totalAudits ?? 0}
        label="Total Audits"
        variant="horizontal"
        backgroundColor="bg-color-assigned"
        textColor="color-assigned"
      />
      <SpaceBetween data-testid="audit-overview-space-between" size="m">
        {/* Status indicators row */}
        <div className="status-wrapper" data-testid="status-wrapper">
          <StatusIndicator
            data-testid="completed-audits-indicator"
            value={auditStats?.metrics.completedAudits ?? 0}
            label="Completed"
            variant="vertical"
            backgroundColor="bg-color-completed"
            textColor="color-completed"
            loading={auditStatsLoading}
          />
          <StatusIndicator
            data-testid="in-progress-audits-indicator"
            value={auditStats?.metrics.inProgressAudits ?? 0}
            label="In Progress"
            variant="vertical"
            backgroundColor="bg-color-in-progress"
            textColor="color-in-progress"
            loading={auditStatsLoading}
          />
          <StatusIndicator
            data-testid="not-started-audits-indicator"
            value={auditStats?.metrics.notStartedAudits ?? 0}
            label="Not Started"
            variant="vertical"
            backgroundColor="bg-color-not-started"
            textColor="color-not-started"
            loading={auditStatsLoading}
          />
          <StatusIndicator
            data-testid="expired-audits-indicator"
            value={auditStats?.metrics.expiredAudits ?? 0}
            label="Expired"
            variant="vertical"
            backgroundColor="bg-color-expired"
            textColor="color-expired"
            loading={auditStatsLoading}
          />
        </div>

        {/* Chart section */}
        <Box data-testid="chart-section-box" padding={{ top: 'l' }}>
          <div
            data-testid="chart-container-wrapper"
            style={{
              minHeight: '300px',
              height: '300px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {/* Conditional rendering: loading, chart, or no data */}
            {auditStatsLoading ? (
              <Box data-testid="loading-box" textAlign="center" color="inherit">
                <Spinner data-testid="loading-spinner" />
              </Box>
            ) : auditStats && auditStats.totalCount > 0 ? (
              <div className="chart-container" data-testid="chart-container">
                <BarChart
                  data-testid="audit-bar-chart"
                  series={series}
                  height={250}
                  i18nStrings={{
                    yTickFormatter: (value) => value.toString(),
                  }}
                  hideFilter
                  hideLegend
                  horizontalBars={false}
                  stackedBars={false}
                  xDomain={auditStats?.divisionNames ?? []}
                />
              </div>
            ) : (
              <Box data-testid="no-data-box" textAlign="center" color="inherit">
                <b>No Audit data available</b>
              </Box>
            )}
          </div>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default AuditOverviewSection;
